import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import _ from 'underscore';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
	Grid, Card, CardContent, Typography, LinearProgress,
	InputBase, Button, Tooltip, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Alert } from 'components';

import KPIsOverviewChart, { CHART_COLORS } from './KPIsOverviewChart';

/*
 * Tecto de seguranca: mesmo que a API devolva series para mais KPIs do que o
 * formulario permite escolher, nunca desenhamos mais graficos do que isto.
 * Cada grafico e um <canvas>; algumas dezenas chegam para matar o separador.
 */
const MAX_CHARTS = 8;

/* Mesma font do gráfico (Chart.js), para a legenda lateral ficar consistente. */
const FONT_FAMILY = "'Roboto', 'Helvetica', 'Arial', sans-serif";

const useStyles = makeStyles((theme) => ({
	container: {
		marginTop: theme.spacing(3)
	},
	placeholder: {
		padding: theme.spacing(6),
		textAlign: 'center'
	},
	chartsColumn: {
		// os graficos empilhados ficam nesta coluna; a legenda ao lado
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3)
	},
	/* --- legenda lateral partilhada --- */
	legend: {
		position: 'sticky',
		top: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		maxHeight: `calc(100vh - ${theme.spacing(4)})`
	},
	legendHeader: {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		padding: theme.spacing(2, 2, 1, 2)
	},
	legendTitle: {
		fontFamily: FONT_FAMILY,
		fontWeight: 600
	},
	legendCount: {
		fontFamily: FONT_FAMILY,
		fontSize: 12,
		color: theme.palette.text.secondary
	},
	search: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		margin: theme.spacing(0, 2, 1, 2),
		padding: theme.spacing(0.5, 1),
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: (theme.palette.action && theme.palette.action.hover) || 'rgba(0,0,0,0.03)'
	},
	searchIcon: {
		fontSize: 18,
		color: theme.palette.text.secondary
	},
	searchInput: {
		fontFamily: FONT_FAMILY,
		fontSize: 13,
		flexGrow: 1
	},
	list: {
		overflowY: 'auto',
		padding: theme.spacing(0, 1, 1, 1),
		flexGrow: 1
	},
	item: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		width: '100%',
		border: 'none',
		background: 'none',
		textAlign: 'left',
		cursor: 'pointer',
		borderRadius: 6,
		padding: theme.spacing(0.75, 1),
		font: 'inherit',
		color: theme.palette.text.primary,
		'&:hover': {
			backgroundColor: (theme.palette.action && theme.palette.action.hover) || 'rgba(0,0,0,0.04)'
		}
	},
	itemDimmed: {
		// quando ha filtro activo, as series escondidas ficam esbatidas
		opacity: 0.35
	},
	swatch: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		flexShrink: 0
	},
	itemLabel: {
		fontFamily: FONT_FAMILY,
		fontSize: 13,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	legendFooter: {
		padding: theme.spacing(1, 2, 2, 2)
	},
	empty: {
		fontFamily: FONT_FAMILY,
		padding: theme.spacing(2),
		fontSize: 13,
		color: theme.palette.text.secondary,
		textAlign: 'center'
	}
}));

/*
 * Legenda lateral unica, partilhada por todos os graficos.
 *
 * Substitui as legendas internas do Chart.js, que desapareciam quando havia
 * muitos sites. Clicar num item filtra TODOS os graficos ao mesmo tempo. Cada
 * item e uma serie: por site (modo separate) ou por site+KPI (modo dual).
 */
const SharedLegend = (props) => {
	const { items, colorMap, shownGroupings, onToggle, onReset } = props;
	const classes = useStyles();
	const [query, setQuery] = useState('');

	const hasFilter = shownGroupings.length > 0;
	const isShown = (key) => !hasFilter || shownGroupings.includes(key);

	const filtered = query
		? items.filter(item => item.label.toLowerCase().includes(query.toLowerCase()))
		: items;

	return (
		<Card className={classes.legend}>
			<div className={classes.legendHeader}>
				<Typography className={classes.legendTitle} variant="subtitle1">
					Series
				</Typography>
				<span className={classes.legendCount}>
					{hasFilter ? `${shownGroupings.length}/${items.length}` : items.length}
				</span>
			</div>

			<div className={classes.search}>
				<SearchIcon className={classes.searchIcon} />
				<InputBase
					className={classes.searchInput}
					placeholder="Filter series…"
					value={query}
					onChange={event => setQuery(event.target.value)}
				/>
				{query && (
					<IconButton size="small" onClick={() => setQuery('')}>
						<CloseIcon className={classes.searchIcon} />
					</IconButton>
				)}
			</div>

			<div className={classes.list}>
				{filtered.length === 0 && (
					<div className={classes.empty}>No series match “{query}”.</div>
				)}
				{filtered.map((item) => (
					<Tooltip key={item.key} title={item.label} placement="left" enterDelay={600}>
						<button
							type="button"
							className={clsx(classes.item, !isShown(item.key) && classes.itemDimmed)}
							onClick={() => onToggle(item.key)}
						>
							<span
								className={classes.swatch}
								style={{ backgroundColor: colorMap[item.key] }}
							/>
							<span className={classes.itemLabel}>{item.label}</span>
						</button>
					</Tooltip>
				))}
			</div>

			{hasFilter && (
				<div className={classes.legendFooter}>
					<Button size="small" fullWidth variant="outlined" onClick={onReset}>
						Show all
					</Button>
				</div>
			)}
		</Card>
	);
};

/*
 * Um grafico por KPI (modo 'separate') OU um unico grafico de duplo eixo,
 * comparando dois KPIs (modo 'dual'). Dentro de cada grafico, uma linha por
 * membro do nivel de agregacao escolhido (site, sector, frequencia ou celula).
 */
const KPIsOverviewCharts = () => {
	const classes = useStyles();

	const result = useSelector(state => state.kpisOverviewReducer.result);
	const hasResult = useSelector(state => state.kpisOverviewReducer.hasResult);
	const isLoading = useSelector(state => state.kpisOverviewReducer.isLoading);
	const metadata = useSelector(state => state.kpisOverviewReducer.metadata);
	/*
	 * O Work Day e o modo de grafico vem do formulario (nao da API), por isso
	 * reagem logo a mudancas sem precisar de novo Apply.
	 */
	const workDay = useSelector(state => state.kpisOverviewReducer.formState.values.workDay);
	const chartMode = useSelector(state => state.kpisOverviewReducer.formState.values.chartMode);
	const y1KpiId = useSelector(state => state.kpisOverviewReducer.formState.values.y1KpiId);
	const y2KpiId = useSelector(state => state.kpisOverviewReducer.formState.values.y2KpiId);

	/*
	 * Filtro de legenda PARTILHADO por todos os graficos. Vazio = tudo visivel.
	 */
	const [shownGroupings, setShownGroupings] = useState([]);

	/* Nova consulta (novo resultado) -> limpa o filtro de legenda. */
	useEffect(() => {
		setShownGroupings([]);
	}, [result]);

	const _toggleGrouping = (grouping) => {
		setShownGroupings((previous) => {
			if (!previous.length) {
				return [grouping];
			}
			if (previous.includes(grouping)) {
				return previous.filter(item => item !== grouping);
			}
			return [...previous, grouping];
		});
	};

	const _resetGroupings = () => setShownGroupings([]);

	/*
	 * Identidade de cada serie na legenda e na cor:
	 *   - modo 'separate': por site (grouping) -> o mesmo site tem sempre a mesma
	 *     cor em todos os graficos de KPI e na legenda lateral;
	 *   - modo 'dual': por AGREGACAO + KPI (ex.: "12385 · VoLTE SSR") -> cada
	 *     combinacao recebe a sua propria cor, porque no mesmo grafico convivem
	 *     dois KPIs para os mesmos sites.
	 */
	const isDual = chartMode === 'dual';
	const technology = (result && metadata.catalogue)
		? metadata.catalogue[result.technology]
		: null;
	const kpiTextOf = (kpiId) => {
		const kpi = technology ? technology.kpis.find(item => item.value === kpiId) : null;
		return kpi ? kpi.text : kpiId;
	};

	const series = (hasResult && result && result.series) || [];

	/* Itens da legenda (chave + rotulo), na ordem em que se atribuem as cores. */
	const legendItems = useMemo(() => {
		const map = {};
		const order = [];
		series.forEach((serie) => {
			const key = isDual ? `${serie.grouping}::${serie.kpiId}` : String(serie.grouping);
			if (!(key in map)) {
				map[key] = {
					key,
					label: isDual
						? `${serie.grouping} · ${kpiTextOf(serie.kpiId)}`
						: String(serie.grouping)
				};
				order.push(key);
			}
		});
		order.sort((a, b) => map[a].label.localeCompare(map[b].label, undefined, { numeric: true }));
		return order.map(key => map[key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [series, isDual, technology]);

	const colorMap = useMemo(() => {
		const map = {};
		legendItems.forEach((item, index) => {
			map[item.key] = CHART_COLORS[index % CHART_COLORS.length];
		});
		return map;
	}, [legendItems]);

	if (isLoading) {
		return (
			<div className={classes.container}>
				<LinearProgress />
			</div>
		);
	}

	if (!hasResult) {
		return (
			<Card className={classes.container}>
				<CardContent className={classes.placeholder}>
					<Typography variant="body1" color="textSecondary">
						Choose the filters above and click Apply.
					</Typography>
				</CardContent>
			</Card>
		);
	}

	if (!result.series.length) {
		return (
			<Card className={classes.container}>
				<CardContent className={classes.placeholder}>
					<Typography variant="body1" color="textSecondary">
						No data found for the selected filters.
					</Typography>
				</CardContent>
			</Card>
		);
	}

	const seriesByKpi = _.groupBy(result.series, 'kpiId');

	// so vale a pena mostrar a legenda lateral quando ha mais do que uma serie
	const showLegend = legendItems.length > 1;

	const findKpi = (kpiId) => (
		technology ? technology.kpis.find(item => item.value === kpiId) : null
	);

	let charts;

	if (isDual) {
		/* Duplo eixo: um unico grafico comparando Y1 e Y2. */
		const kpi1 = findKpi(y1KpiId);
		const kpi2 = findKpi(y2KpiId);
		const series1 = seriesByKpi[y1KpiId] || [];
		const series2 = seriesByKpi[y2KpiId] || [];

		if (!series1.length || !series2.length) {
			charts = (
				<div className={classes.chartsColumn}>
					<Alert
						variant="warning"
						message="Select the primary (Y1) and secondary (Y2) KPIs and click Apply to load the dual-axis chart."
					/>
				</div>
			);
		} else {
			charts = (
				<div className={classes.chartsColumn}>
					<KPIsOverviewChart
						chartKey="dual"
						kpi={kpi1}
						series={series1}
						kpi2={kpi2}
						series2={series2}
						buckets={result.buckets}
						granularity={result.granularity}
						workDay={workDay}
						shownGroupings={shownGroupings}
						colorMap={colorMap}
					/>
				</div>
			);
		}
	} else {
		/* Modo original: um grafico por KPI (ate MAX_CHARTS). */
		const allKpiIds = Object.keys(seriesByKpi);
		const kpiIds = allKpiIds.slice(0, MAX_CHARTS);

		charts = (
			<div className={classes.chartsColumn}>
				{allKpiIds.length > MAX_CHARTS && (
					<Alert
						variant="warning"
						message={`Showing the first ${MAX_CHARTS} of ${allKpiIds.length} KPIs. Narrow your selection to see the others.`}
					/>
				)}
				{kpiIds.map((kpiId) => (
					<KPIsOverviewChart
						key={kpiId}
						chartKey={kpiId}
						kpi={findKpi(kpiId)}
						series={seriesByKpi[kpiId]}
						buckets={result.buckets}
						granularity={result.granularity}
						workDay={workDay}
						shownGroupings={shownGroupings}
						colorMap={colorMap}
					/>
				))}
			</div>
		);
	}

	if (!showLegend) {
		return <div className={classes.container}>{charts}</div>;
	}

	return (
		<Grid className={classes.container} container spacing={3}>
			<Grid item xs={12} lg={9} xl={10}>
				{charts}
			</Grid>
			<Grid item xs={12} lg={3} xl={2}>
				<SharedLegend
					items={legendItems}
					colorMap={colorMap}
					shownGroupings={shownGroupings}
					onToggle={_toggleGrouping}
					onReset={_resetGroupings}
				/>
			</Grid>
		</Grid>
	);
};

export default KPIsOverviewCharts;